import { useEffect, useState } from 'react';
import { fetchListOrders } from '@/api/orderService';
import { useSentryQuery } from '@/utils/sentryQueryWrapper';

const DEBOUNCE_MS = 300;

/**
 * BOXY-409 — how many orders an export query matches: the list endpoint's
 * `total` at `perpage=1`. Debounced so typing through dates and statuses
 * does not fire a request per change. A null query is not asked.
 */
export const useOrderExportCount = (query: string | null) => {
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const { data, isFetching } = useSentryQuery<number, Error>(
    ['order-export-count', debounced],
    async () =>
      (await fetchListOrders(1, 1, undefined, new URLSearchParams(debounced ?? ''))).object.total,
    { enabled: debounced !== null && query !== null, staleTime: 30_000 },
  );

  return {
    count: query === null ? undefined : data,
    isCounting: query !== null && (debounced !== query || isFetching),
  };
};
